import { Component, Input, Output, EventEmitter, signal, computed, inject, OnInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';
import { ArticleService } from '../../../shared/services/article.service';
import { ARTICLE_ROUTE_PREFIX, formatSlugAsTitle, titleToSlug } from '../../../shared/utils/wikilink.utils';
import { ArticleSummary } from '../../../types/article.interface';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

export interface WikilinkPopupData {
  slug: string;
  displayText: string | null;
  anchorEl: HTMLElement;
  /** Whether the user can edit the wikilink (editable markdown context) */
  editable: boolean;
  /** If true, automatically opens in slug editing mode with search (for new insertions) */
  autoEditSlug?: boolean;
  /** Initial search query to pre-fill (e.g. selected text before slug conversion) */
  initialSearchQuery?: string;
}

export interface WikilinkChange {
  oldSlug: string;
  newSlug: string;
  newDisplayText: string | null;
}

@Component({
  selector: 'app-wikilink-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './wikilink-popup.component.html',
  styleUrls: ['./wikilink-popup.component.scss']
})
export class WikilinkPopupComponent implements OnInit, OnDestroy {
  @Input() data!: WikilinkPopupData;
  @Output() navigate = new EventEmitter<string>();
  @Output() create = new EventEmitter<string>();
  @Output() changed = new EventEmitter<WikilinkChange>();
  @Output() unlink = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  private readonly router = inject(Router);
  private readonly articleService = inject(ArticleService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();

  // State
  slugInput = signal<string>('');
  displayTextInput = signal<string>('');
  isEditingSlug = signal<boolean>(false);
  isEditingText = signal<boolean>(false);
  showDropdown = signal<boolean>(false);
  dropdownResults = signal<ArticleSummary[]>([]);
  loadingResults = signal<boolean>(false);
  selectedIndex = signal<number>(-1);

  // Resolved info for current slug
  articleExists = signal<boolean>(false);
  articleTitle = signal<string>('');

  // Search subject for debounced autocomplete
  private searchSubject = new Subject<string>();

  // Position
  popupStyle = signal<{ top: string; left: string; }>({ top: '0px', left: '0px' });

  editable = computed(() => this.data?.editable ?? false);

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const el = this.elementRef.nativeElement as HTMLElement;
    if (!el.contains(event.target as Node) && !this.data?.anchorEl?.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.close();
  }

  ngOnInit(): void {
    if (!this.data) return;

    this.slugInput.set(this.data.slug);
    this.displayTextInput.set(this.data.displayText || '');

    // Position popup near the anchor element
    this.positionPopup();

    // Resolve current slug
    this.resolveSlug(this.data.slug);

    // Auto-open slug editing if requested (e.g. toolbar insertion)
    if (this.data.autoEditSlug) {
      this.isEditingSlug.set(true);
      const query = this.data.initialSearchQuery || this.data.slug;
      this.slugInput.set(query);
      // Trigger search immediately
      if (query.length >= 2) {
        this.searchSubject.next(query);
      }
      setTimeout(() => {
        const input = this.elementRef.nativeElement.querySelector('.slug-edit-input') as HTMLInputElement;
        input?.focus();
        input?.select();
      }, 50);
    }

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(query => {
        this.loadingResults.set(true);
        this.selectedIndex.set(-1);
        return this.articleService.searchArticles(query);
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.dropdownResults.set(results);
      this.loadingResults.set(false);
      this.showDropdown.set(results.length > 0);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private positionPopup(): void {
    if (!this.data?.anchorEl) return;
    const rect = this.data.anchorEl.getBoundingClientRect();

    // Position below the anchor, constrained to viewport
    const top = rect.bottom + 6;
    let left = rect.left;

    // Constrain to viewport width (popup is ~320px wide)
    const popupWidth = 320;
    if (left + popupWidth > window.innerWidth - 16) {
      left = window.innerWidth - popupWidth - 16;
    }
    if (left < 16) left = 16;

    this.popupStyle.set({
      top: `${top}px`,
      left: `${left}px`,
    });
  }

  private resolveSlug(slug: string): void {
    this.articleService.lookupSlugs([slug]).subscribe(map => {
      const info = map.get(slug);
      this.articleExists.set(info?.exists ?? false);
      this.articleTitle.set(info?.title || formatSlugAsTitle(slug));
    });
  }

  // --- Slug editing ---

  startEditSlug(): void {
    if (!this.editable()) return;
    this.isEditingSlug.set(true);
    this.showDropdown.set(false);
    // Trigger initial search
    setTimeout(() => {
      const input = this.elementRef.nativeElement.querySelector('.slug-edit-input') as HTMLInputElement;
      input?.focus();
      input?.select();
    }, 0);
  }

  onSlugInput(value: string): void {
    this.slugInput.set(value);
    // Convert to slug format live
    const asSlug = titleToSlug(value);
    if (asSlug.length >= 2) {
      this.searchSubject.next(value);
    } else {
      this.showDropdown.set(false);
      this.dropdownResults.set([]);
    }
  }

  onSlugKeydown(event: KeyboardEvent): void {
    const results = this.dropdownResults();
    const idx = this.selectedIndex();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex.set(Math.min(idx + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex.set(Math.max(idx - 1, -1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (idx >= 0 && idx < results.length) {
        this.selectResult(results[idx]);
      } else {
        this.confirmSlugEdit();
      }
    }
  }

  selectResult(result: ArticleSummary): void {
    this.slugInput.set(result.slug);
    this.articleExists.set(true);
    this.articleTitle.set(result.title);
    this.showDropdown.set(false);
    this.isEditingSlug.set(false);
    this.emitChange();
  }

  confirmSlugEdit(): void {
    const newSlug = titleToSlug(this.slugInput());
    if (newSlug && newSlug !== this.data.slug) {
      this.slugInput.set(newSlug);
      this.resolveSlug(newSlug);
      this.emitChange();
    }
    this.isEditingSlug.set(false);
    this.showDropdown.set(false);
  }

  cancelSlugEdit(): void {
    this.slugInput.set(this.data.slug);
    this.isEditingSlug.set(false);
    this.showDropdown.set(false);
  }

  // --- Display text editing ---

  startEditText(): void {
    if (!this.editable()) return;
    this.isEditingText.set(true);
    setTimeout(() => {
      const input = this.elementRef.nativeElement.querySelector('.text-edit-input') as HTMLInputElement;
      input?.focus();
      input?.select();
    }, 0);
  }

  confirmTextEdit(): void {
    this.isEditingText.set(false);
    this.emitChange();
  }

  onTextKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmTextEdit();
    }
  }

  // --- Actions ---

  navigateToArticle(): void {
    const slug = this.slugInput();
    if (this.articleExists()) {
      this.navigate.emit(slug);
      this.router.navigateByUrl(`${ARTICLE_ROUTE_PREFIX}/${slug}`);
    } else {
      this.create.emit(slug);
    }
    this.close();
  }

  removeWikilink(): void {
    this.unlink.emit(this.data.slug);
    this.close();
  }

  private emitChange(): void {
    this.changed.emit({
      oldSlug: this.data.slug,
      newSlug: this.slugInput(),
      newDisplayText: this.displayTextInput() || null
    });
  }

  close(): void {
    this.closed.emit();
  }
}
